import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { supabase } from '../../services/supabase'
import { isAdmin } from '../../utils/jwt'

export const ErrorMessage = ({ message, trace, type = 'error' }) => {
  const isWarning = type === 'warning'
  const [showTrace, setShowTrace] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    // Check if user is admin to show stack trace
    const checkAdminStatus = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (session?.access_token && trace) {
        setShowTrace(isAdmin(session.access_token))
      }
    }
    checkAdminStatus()
  }, [trace])

  const ErrorIcon = () => (
    <svg
      className="w-5 h-5 flex-shrink-0 mt-0.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  )

  const WarningIcon = () => (
    <svg
      className="w-5 h-5 flex-shrink-0 mt-0.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  )

  return (
    <div
      className={`flex items-start space-x-3 p-4 rounded-lg border ${
        isWarning
          ? 'bg-yellow-900/20 border-yellow-800/50 text-yellow-200'
          : 'bg-red-900/20 border-red-800/50 text-red-200'
      }`}
    >
      {isWarning ? <WarningIcon /> : <ErrorIcon />}
      <div className="flex-1">
        <h4 className="font-medium text-sm mb-1">{isWarning ? 'Warning' : 'Error'}</h4>
        <p className="text-sm leading-relaxed opacity-90">{message}</p>
        {showTrace && trace && (
          <div className="mt-3 pt-3 border-t border-red-800/30">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs font-medium text-red-300 cursor-pointer hover:text-red-200 transition-colors flex items-center focus:outline-none focus:text-red-200"
              aria-expanded={isExpanded}
              aria-controls="stack-trace-content"
            >
              <span
                className={`mr-2 text-red-400 inline-block transition-transform duration-300 ease-out ${
                  isExpanded ? 'rotate-90' : ''
                }`}
              >
                ▶
              </span>
              Stack Trace
            </button>
            <div
              id="stack-trace-content"
              className={`grid transition-all duration-500 ease-out ${
                isExpanded ? 'grid-rows-[1fr] mt-3' : 'grid-rows-[0fr]'
              }`}
            >
              <div className="overflow-hidden">
                <div
                  className={`bg-gray-950/50 rounded-lg border border-gray-800 transition-all duration-300 ${
                    isExpanded
                      ? 'opacity-100 transform translate-y-0'
                      : 'opacity-0 transform -translate-y-2'
                  }`}
                >
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <tbody>
                        {trace.split('\n').map((line, index) => {
                          // Style different parts of the stack trace
                          const isFilePath = line.includes('File "') || line.includes('at ')
                          const isErrorMessage =
                            line.includes('Exception:') || line.includes('Error:')
                          const isTraceback = line.includes('Traceback')
                          const isCodeLine = line.match(/^\s{2,}[a-zA-Z]/) // Indented code lines
                          const isEmpty = line.trim() === ''

                          let lineClass = 'font-mono'
                          let lineContent = line

                          if (isErrorMessage) {
                            lineClass += ' text-red-300 font-semibold'
                          } else if (isFilePath) {
                            lineClass += ' text-blue-400'
                            // Highlight line numbers in file paths
                            lineContent = line.replace(
                              /line (\d+)/,
                              'line <span class="text-yellow-400 font-semibold">$1</span>'
                            )
                          } else if (isTraceback) {
                            lineClass += ' text-yellow-400 font-semibold border-b border-gray-700'
                          } else if (isCodeLine) {
                            lineClass += ' text-green-400 bg-gray-900/30'
                          } else if (isEmpty) {
                            lineClass += ' h-2'
                          } else {
                            lineClass += ' text-gray-300'
                          }

                          return (
                            <tr key={index} className="hover:bg-gray-900/30 group">
                              <td className="text-gray-600 text-xs font-mono px-3 py-0.5 text-right select-none border-r border-gray-800 w-12">
                                {!isEmpty && index + 1}
                              </td>
                              <td className={`text-xs px-3 py-0.5 ${lineClass}`}>
                                {isFilePath ? (
                                  <span dangerouslySetInnerHTML={{ __html: lineContent }} />
                                ) : (
                                  lineContent || ' '
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

ErrorMessage.propTypes = {
  message: PropTypes.string.isRequired,
  trace: PropTypes.string,
  type: PropTypes.oneOf(['error', 'warning']),
}

ErrorMessage.defaultProps = {
  type: 'error',
  trace: null,
}
